import { SignedIn, SignedOut, SignInButton, SignUpButton, UserButton } from "@clerk/clerk-react";
import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { GlassFilter } from '@/components/ui/liquid-glass';
import { Shield, ExternalLink } from 'lucide-react';
import logoWhite from '@/assets/open-insurance-logo-white.png';

export default function Login() {
  const navigate = useNavigate();
  const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
  const isValidClerkKey = PUBLISHABLE_KEY && PUBLISHABLE_KEY !== "YOUR_PUBLISHABLE_KEY" && PUBLISHABLE_KEY.startsWith("pk_");

  useEffect(() => {
    // Redirect authenticated users to dashboard automatically
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{
        background: `
          radial-gradient(circle at 20% 80%, hsl(13, 97%, 51%, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, hsl(13, 97%, 51%, 0.08) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, hsl(13, 97%, 51%, 0.06) 0%, transparent 50%),
          linear-gradient(135deg, 
            hsl(220, 30%, 1%) 0%, 
            hsl(200, 40%, 2%) 25%, 
            hsl(190, 50%, 3%) 50%, 
            hsl(180, 60%, 1%) 75%, 
            hsl(220, 30%, 0%) 100%
          )
        `
      }}
    >
      {/* Glass Filter for distortion effects */}
      <GlassFilter />
      
      {/* Tron Grid Overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `
            linear-gradient(90deg, transparent 98%, hsl(13, 97%, 51%, 0.15) 100%),
            linear-gradient(0deg, transparent 98%, hsl(13, 97%, 51%, 0.15) 100%)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridSlide 20s linear infinite'
        }}
      />
      
      {/* Subtle glow overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-red-500/5" />
      
      <div className="relative z-10 w-full max-w-md space-y-6 animate-fade-in">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center">
            <img 
              src={logoWhite} 
              alt="Open Insurance Logo" 
              className="h-12 w-auto object-contain filter drop-shadow-lg"
            />
          </div>
          <div>
            <p className="text-lg text-muted-foreground">Open Insurance Access</p>
          </div>
        </div>

        {/* Cyberpunk Glitch Glass Form */}
        <div className="w-full max-w-md bg-black/80 backdrop-blur-sm border border-[#ff3904]/20 shadow-[0_0_20px_rgba(255,57,4,0.1)] overflow-hidden">
          {/* Card Header */}
          <div className="flex justify-between items-center bg-black/30 p-3 border-b border-[#ff3904]/20">
            <div className="flex items-center gap-2 text-[#ff3904] text-xs font-bold uppercase tracking-wider">
              <Shield className="w-4 h-4" />
              <span>SECURE_ACCESS</span>
            </div>
            <div className="flex gap-1">
              <span className="w-2 h-2 rounded-full bg-gray-600"></span>
              <span className="w-2 h-2 rounded-full bg-gray-600"></span>
              <span className="w-2 h-2 rounded-full bg-gray-600"></span>
            </div>
          </div>

          {/* Card Body */}
          <div className="p-6">
            {isValidClerkKey ? (
              <>
                <SignedOut>
                  <div className="space-y-6">
                    <SignInButton>
                      <button className="relative w-full p-3 bg-transparent border-2 border-[#ff3904] text-[#ff3904] font-mono font-bold uppercase tracking-widest text-sm transition-all duration-300 overflow-hidden group hover:bg-[#ff3904] hover:text-black hover:shadow-[0_0_25px_rgba(255,57,4,0.5)] active:scale-95">
                        <span className="relative z-10 transition-opacity duration-200 group-hover:opacity-0">
                          INITIATE_CONNECTION
                        </span>
                        <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-[#ff3904] text-black">
                          INITIATE_CONNECTION
                        </span>
                      </button>
                    </SignInButton>

                    <SignUpButton>
                      <button className="relative w-full p-3 bg-transparent border-2 border-[#ff3904]/50 text-[#ff3904]/70 font-mono font-bold uppercase tracking-widest text-sm transition-all duration-300 overflow-hidden hover:border-[#ff3904] hover:text-[#ff3904] active:scale-95">
                        CREATE_ACCOUNT
                      </button>
                    </SignUpButton>
                  </div>
                </SignedOut>
                
                <SignedIn>
                  <div className="text-center space-y-6">
                    <div className="flex items-center justify-center">
                      <UserButton 
                        appearance={{
                          elements: {
                            avatarBox: "w-12 h-12 ring-2 ring-[#ff3904]/30 hover:ring-[#ff3904]",
                            userButtonPopoverCard: "bg-black/90 border border-[#ff3904]/20",
                            userButtonPopoverText: "text-[#ff3904] font-mono",
                          }
                        }}
                      />
                    </div>
                    <p className="text-[#ff3904]/80 font-mono text-sm uppercase tracking-wider">
                      ACCESS_GRANTED
                    </p>
                    <Button 
                      onClick={() => navigate('/dashboard')}
                      className="relative w-full p-3 bg-transparent border-2 border-[#ff3904] text-[#ff3904] font-mono font-bold uppercase tracking-widest text-sm transition-all duration-300 overflow-hidden group hover:bg-[#ff3904] hover:text-black hover:shadow-[0_0_25px_rgba(255,57,4,0.5)] active:scale-95"
                    >
                      <span className="relative z-10 transition-opacity duration-200 group-hover:opacity-0">
                        ENTER_WORKBENCH
                      </span>
                      <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-[#ff3904] text-black">
                        ENTER_WORKBENCH
                      </span>
                    </Button>
                  </div>
                </SignedIn>
              </>
            ) : (
              <div className="space-y-6">
                <div className="text-center space-y-4">
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded">
                    <p className="text-yellow-400 font-mono text-xs uppercase tracking-wider mb-2">
                      CLERK_KEY_REQUIRED
                    </p>
                    <p className="text-yellow-400/80 text-sm font-mono">
                      Authentication requires a valid Clerk publishable key
                    </p>
                  </div>
                  <a
                    href="https://dashboard.clerk.com/last-active?path=api-keys"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#ff3904] hover:text-[#ff3904]/80 transition-colors font-mono text-sm"
                  >
                    Get Clerk API Key
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
                
          <div className="border-t border-[#ff3904]/20 pt-6">
                  <p className="text-[#ff3904]/60 font-mono text-xs uppercase tracking-wider mb-4 text-center">
                    DEMO_MODE
                  </p>
                  <Button 
                    onClick={() => navigate('/dashboard')}
                    className="relative w-full p-3 bg-transparent border-2 border-[#ff3904]/50 text-[#ff3904]/70 font-mono font-bold uppercase tracking-widest text-sm transition-all duration-300 overflow-hidden hover:border-[#ff3904] hover:text-[#ff3904] active:scale-95"
                  >
                    ENTER_DEMO_MODE
                  </Button>
          </div>
              </div>
            )}
          </div>
        </div>

        {/* Copyright Footer */}
        <div className="mt-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src={logoWhite} 
              alt="Open Insurance Logo" 
              className="h-6 w-auto object-contain opacity-60"
            />
            <div className="h-4 w-px bg-[#ff3904]/30"></div>
            <span className="text-xs text-[#ff3904]/60 font-mono uppercase tracking-wider">
              Open Insurance
            </span>
          </div>
          <p className="text-xs text-muted-foreground/50 font-mono">
            © 2025 - Dominguez Tigranyan Ventures  •  Made with{" "}
            <span
              className="inline-block mx-0.5 text-sm animate-pulse"
              style={{
                background: "linear-gradient(45deg, #ff3904, #ff6b47, #ff3904)",
                backgroundSize: "200% 200%",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                animation: "heartbeat 1.5s ease-in-out infinite, gradientShift 3s ease-in-out infinite"
              }}
            >
              ♥︎
            </span>
            {" "}in Miami
          </p>
        </div>
      </div>
    </div>
  );
}
